import { useEffect, useState, useMemo } from "react";
import { Clock, MapPin, User, CalendarDays, Info } from "lucide-react";
import { motion } from "framer-motion";
import { schools } from "@/data/clubs";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";

const DAYS = ["ראשון", "שני", "שלישי", "רביעי", "חמישי"];

const buildEvents = () =>
  Object.values(schools).flatMap((school) =>
    school.clubs.flatMap((club) =>
      club.schedule.map((s) => ({
        id: `${school.id}-${club.id}-${s.day}-${s.time}`,
        day: s.day,
        time: s.time,
        start: s.time.split("-")[0],
        club: club.name,
        instructor: club.instructor,
        location: school.name,
        schoolId: school.id,
        color: club.color,
        grades: s.grades,
      }))
    )
  );

const buildNotes = () =>
  Object.values(schools).flatMap((school) =>
    school.clubs
      .filter((c) => c.schedule.length === 0)
      .map((c) => ({
        id: `${school.id}-${c.id}`,
        club: c.name,
        location: school.name,
        schoolId: school.id,
        note: c.scheduleNote || "פרטים בהמשך",
      }))
  );

const filters = [
  { id: "all", label: "כל המוקדים" },
  { id: "maayanot", label: "מעיינות" },
  { id: "hadassim", label: "הדסים · צורים" },
];

export default function WeeklyCalendar() {
  const [filter, setFilter] = useState("all");
  const events = useMemo(buildEvents, []);

  useEffect(() => {
    document.title = "לוח שבועי | חוגי ספורט והעשרה";
    window.scrollTo(0, 0);
  }, []);

  const visible = filter === "all" ? events : events.filter((e) => e.schoolId === filter);
  const notes = useMemo(buildNotes, []);
  const visibleNotes = filter === "all" ? notes : notes.filter((n) => n.schoolId === filter);

  return (
    <main data-testid="calendar-page">
      <Navbar />

      <section className="px-4 md:px-10 pt-28 md:pt-36 pb-10">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="flex items-center gap-2 text-accent font-head font-semibold mb-3">
              <CalendarDays className="w-5 h-5" />
              לוח שבועי
            </div>
            <h1 className="font-head font-black text-4xl md:text-6xl tracking-tighter leading-none">
              כל החוגים בשבוע
            </h1>
            <p className="mt-5 text-lg text-ink/60 max-w-2xl leading-relaxed">
              מבט שבועי מרוכז על כל החוגים בשני המוקדים — עם המיקום, השעות ושם המדריך לכל חוג.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-wrap gap-2" data-testid="calendar-filters">
              {filters.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`px-5 py-2.5 rounded-full text-sm font-head font-semibold border transition-colors ${
                    filter === f.id
                      ? "bg-ink text-paper border-ink"
                      : "bg-white text-ink/70 border-ink/15 hover:border-ink"
                  }`}
                  data-testid={`calendar-filter-${f.id}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-4 md:px-10 pb-24">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {DAYS.map((day, di) => {
            const dayEvents = visible
              .filter((e) => e.day === day)
              .sort((a, b) => a.start.localeCompare(b.start));
            return (
              <motion.div
                key={day}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: di * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className="rounded-2xl bg-ink/[0.02] border border-ink/10 overflow-hidden"
                data-testid={`calendar-day-${day}`}
              >
                <div className="bg-ink text-paper px-4 py-3 font-head font-bold text-lg flex items-center justify-between">
                  <span>יום {day}</span>
                  <span className="text-paper/50 text-sm">{dayEvents.length}</span>
                </div>

                <div className="p-3 space-y-3 min-h-[80px]">
                  {dayEvents.length === 0 && (
                    <p className="text-ink/30 text-sm text-center py-6">אין חוגים</p>
                  )}
                  {dayEvents.map((e) => (
                    <div
                      key={e.id}
                      className="rounded-xl bg-white border border-ink/10 p-3.5 pr-4 relative overflow-hidden"
                      style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.03)" }}
                      data-testid={`calendar-event-${e.id}`}
                    >
                      <span className="absolute top-0 bottom-0 right-0 w-1.5" style={{ backgroundColor: e.color }} />
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-head font-extrabold text-ink text-base">{e.club}</h3>
                        <span className="text-[11px] font-semibold text-ink/50 bg-ink/5 px-2 py-0.5 rounded-full whitespace-nowrap">
                          {e.grades}
                        </span>
                      </div>
                      <div className="mt-2 space-y-1.5 text-sm text-ink/70">
                        <div className="flex items-center gap-2" dir="ltr">
                          <Clock className="w-3.5 h-3.5 text-accent shrink-0" />
                          <span className="tabular-nums">{e.time}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3.5 h-3.5 text-accent shrink-0" />
                          <span>{e.location}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <User className="w-3.5 h-3.5 text-accent shrink-0" />
                          <span>{e.instructor}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        {visibleNotes.length > 0 && (
          <div className="max-w-7xl mx-auto mt-8 space-y-3" data-testid="calendar-notes">
            {visibleNotes.map((n) => (
              <div
                key={n.id}
                className="flex items-start gap-3 rounded-xl border border-ink/10 bg-white/60 p-4"
                data-testid={`calendar-note-${n.id}`}
              >
                <Info className="w-5 h-5 text-accent mt-0.5 shrink-0" />
                <p className="text-ink/70 leading-relaxed">
                  <span className="font-head font-bold text-ink">{n.club} {n.location}</span> — {n.note}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </main>
  );
}
